import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Checkbox from './Checkbox';
import {
  ELEMENT_TYPE_MODULE, ELEMENT_TYPE_TITLE, ELEMENT_TYPE_DIVIDER,
  ELEMENT_TYPE_SPACER, ELEMENT_TYPE_INFORMATION, ELEMENT_TYPE_CALCULATION,
} from '../constants';

const STATIC_TYPES = [
  ELEMENT_TYPE_TITLE, ELEMENT_TYPE_DIVIDER, ELEMENT_TYPE_SPACER, ELEMENT_TYPE_INFORMATION,
];

/**
 * Reusable tree-view selector for project pages and variables.
 * Supports cascading checkbox selection, indeterminate state,
 * expand/collapse, filtering, and module (iterative) children.
 *
 * Designed as a generic UI control for grouped actions on variables
 * (e.g. duplication config, bulk move, bulk selection).
 */
class TreeViewSelector extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedIds: new Set(props.initialSelectedIds || []),
      expandedPages: new Set(props.projectPages.map((p) => p.key)),
      expandedModules: new Set(),
      filterSelected: false,
      allExpanded: true,
    };
  }

  getPageVariables = (pageId) => {
    const { projectElements, elements } = this.props;
    return projectElements.filter((pe) => {
      if (pe.project_page !== pageId) return false;
      if (pe.module) return false;
      const element = elements[pe.element];
      if (!element) return false;
      if (STATIC_TYPES.includes(element.type)) return false;
      if (element.type === ELEMENT_TYPE_CALCULATION) return false;
      return true;
    });
  };

  getModuleChildren = (moduleId) => {
    const { projectElements, elements } = this.props;
    return projectElements.filter((pe) => {
      if (pe.module !== moduleId) return false;
      const element = elements[pe.element];
      if (!element) return false;
      if (STATIC_TYPES.includes(element.type)) return false;
      if (element.type === ELEMENT_TYPE_CALCULATION) return false;
      return true;
    });
  };

  // Tri-state selection of a single variable: 'full' | 'partial' | 'none'.
  // For a module, the state is driven by its children (so unchecking one
  // iterative variable turns the module - and its page - into "partial").
  getVariableState = (pe) => {
    const { elements } = this.props;
    const { selectedIds } = this.state;
    const element = elements[pe.element];
    if (element && element.type === ELEMENT_TYPE_MODULE) {
      const children = this.getModuleChildren(pe.id);
      if (children.length === 0) return selectedIds.has(pe.id) ? 'full' : 'none';
      const state = this.getModuleCheckState(pe.id);
      if (state.checked) return 'full';
      if (state.indeterminate) return 'partial';
      return 'none';
    }
    return selectedIds.has(pe.id) ? 'full' : 'none';
  };

  getPageCheckState = (pageId) => {
    const variables = this.getPageVariables(pageId);
    if (variables.length === 0) return { checked: false, indeterminate: false };
    const states = variables.map((v) => this.getVariableState(v));
    const fullCount = states.filter((s) => s === 'full').length;
    const anySelected = states.some((s) => s !== 'none');
    if (!anySelected) return { checked: false, indeterminate: false };
    if (fullCount === variables.length) return { checked: true, indeterminate: false };
    return { checked: false, indeterminate: true };
  };

  getModuleCheckState = (moduleId) => {
    const children = this.getModuleChildren(moduleId);
    const { selectedIds } = this.state;
    if (children.length === 0) return { checked: false, indeterminate: false };
    const selectedCount = children.filter((v) => selectedIds.has(v.id)).length;
    if (selectedCount === 0) return { checked: false, indeterminate: false };
    if (selectedCount === children.length) return { checked: true, indeterminate: false };
    return { checked: false, indeterminate: true };
  };

  getSelectedIds = () => Array.from(this.state.selectedIds);

  getTotalSelectableCount = () => {
    const { projectPages } = this.props;
    let total = 0;
    projectPages.forEach((page) => {
      total += this.getPageVariables(page.key).length;
    });
    return total;
  };

  getModuleIds = () => {
    const { projectElements, elements } = this.props;
    return projectElements
      .filter((pe) => {
        const el = elements[pe.element];
        return el && el.type === ELEMENT_TYPE_MODULE;
      })
      .map((pe) => pe.id);
  };

  setSelectedIds = (ids) => {
    this.setState({ selectedIds: new Set(ids) });
  };

  resetState = () => {
    const { projectPages, initialSelectedIds } = this.props;
    this.setState({
      selectedIds: new Set(initialSelectedIds || []),
      expandedPages: new Set(projectPages.map((p) => p.key)),
      expandedModules: new Set(this.getModuleIds()),
      filterSelected: false,
      allExpanded: true,
    });
  };

  togglePage = (pageId, forceChecked) => {
    const { elements } = this.props;
    const variables = this.getPageVariables(pageId);
    const { selectedIds } = this.state;
    const newSelected = new Set(selectedIds);
    const shouldCheck = forceChecked !== undefined
      ? forceChecked : !this.getPageCheckState(pageId).checked;

    variables.forEach((v) => {
      if (shouldCheck) {
        newSelected.add(v.id);
      } else {
        newSelected.delete(v.id);
      }
      // Cascade into module children
      const el = elements[v.element];
      if (el && el.type === ELEMENT_TYPE_MODULE) {
        this.getModuleChildren(v.id).forEach((child) => {
          if (shouldCheck) {
            newSelected.add(child.id);
          } else {
            newSelected.delete(child.id);
          }
        });
      }
    });
    this.setState({ selectedIds: newSelected });
  };

  toggleVariable = (varId, forceChecked) => {
    const { selectedIds } = this.state;
    const newSelected = new Set(selectedIds);
    const shouldCheck = forceChecked !== undefined
      ? forceChecked : !newSelected.has(varId);
    if (shouldCheck) {
      newSelected.add(varId);
    } else {
      newSelected.delete(varId);
    }
    this.setState({ selectedIds: newSelected });
  };

  toggleModule = (moduleId, forceChecked) => {
    const children = this.getModuleChildren(moduleId);
    const { selectedIds } = this.state;
    const newSelected = new Set(selectedIds);
    const shouldCheck = forceChecked !== undefined
      ? forceChecked : !this.getModuleCheckState(moduleId).checked;

    if (shouldCheck) {
      newSelected.add(moduleId);
    } else {
      newSelected.delete(moduleId);
    }
    children.forEach((v) => {
      if (shouldCheck) {
        newSelected.add(v.id);
      } else {
        newSelected.delete(v.id);
      }
    });
    this.setState({ selectedIds: newSelected });
  };

  toggleExpanded = (pageId) => {
    const { expandedPages } = this.state;
    const newExpanded = new Set(expandedPages);
    if (newExpanded.has(pageId)) {
      newExpanded.delete(pageId);
    } else {
      newExpanded.add(pageId);
    }
    this.setState({ expandedPages: newExpanded });
  };

  toggleModuleExpanded = (moduleId) => {
    const { expandedModules } = this.state;
    const newExpanded = new Set(expandedModules);
    if (newExpanded.has(moduleId)) {
      newExpanded.delete(moduleId);
    } else {
      newExpanded.add(moduleId);
    }
    this.setState({ expandedModules: newExpanded });
  };

  toggleAllExpanded = () => {
    const { projectPages } = this.props;
    const { allExpanded } = this.state;
    if (allExpanded) {
      this.setState({
        expandedPages: new Set(),
        expandedModules: new Set(),
        allExpanded: false,
      });
    } else {
      this.setState({
        expandedPages: new Set(projectPages.map((p) => p.key)),
        expandedModules: new Set(this.getModuleIds()),
        allExpanded: true,
      });
    }
  };

  renderVariable = (pe, indent = false) => {
    const { elements, t, idPrefix } = this.props;
    const { selectedIds } = this.state;
    const element = elements[pe.element];
    if (!element) return null;
    const isModule = element.type === ELEMENT_TYPE_MODULE;

    return (
      <div key={pe.id} className={`d-flex align-items-center${indent ? ' ml-4' : ''}`}>
        <Checkbox
          id={`${idPrefix}-var-${pe.id}`}
          checked={selectedIds.has(pe.id)}
          onChange={(e) => this.toggleVariable(pe.id, e.target.checked)}
          className="mt-0 mb-2"
        >
          <span
            className="cursor-pointer duplication-tree-label"
            role="button"
            tabIndex={0}
          >
            {element.name || t('common:elements.default-labels.variable')}
          </span>
          {isModule && (
            <FontAwesomeIcon
              icon={['far', 'sitemap']}
              className="ml-2 text-muted"
              transform="shrink-3"
            />
          )}
        </Checkbox>
      </div>
    );
  };

  renderModuleWithChildren = (pe) => {
    const { elements, t, idPrefix } = this.props;
    const { selectedIds, filterSelected } = this.state;
    const element = elements[pe.element];
    if (!element) return null;
    const children = this.getModuleChildren(pe.id);
    const moduleState = this.getModuleCheckState(pe.id);
    const moduleChecked = moduleState.checked
      || (children.length === 0 && selectedIds.has(pe.id));
    const visibleChildren = filterSelected
      ? children.filter((child) => selectedIds.has(child.id))
      : children;

    const isModuleExpanded = this.state.expandedModules.has(pe.id);

    return (
      <div key={pe.id} className="mb-2" style={{ position: 'relative' }}>
        <div className="d-flex align-items-center">
          <button
            type="button"
            className="btn btn-link btn-sm p-0"
            style={{
              lineHeight: 1,
              position: 'absolute',
              left: '-1.2rem',
              top: '2px',
            }}
            onClick={() => this.toggleModuleExpanded(pe.id)}
          >
            <FontAwesomeIcon
              icon={['far', isModuleExpanded ? 'chevron-down' : 'chevron-right']}
              transform="shrink-3"
            />
          </button>
          <Checkbox
            id={`${idPrefix}-var-${pe.id}`}
            checked={moduleChecked}
            indeterminate={moduleState.indeterminate}
            onChange={(e) => this.toggleModule(pe.id, e.target.checked)}
            className="mt-0 mb-2"
          >
            <span
              className="cursor-pointer duplication-tree-label"
              role="button"
              tabIndex={0}
            >
              {element.name || t('common:elements.default-labels.variable')}
            </span>
            <FontAwesomeIcon
              icon={['far', 'sitemap']}
              className="ml-2 text-muted"
              transform="shrink-3"
            />
          </Checkbox>
        </div>
        {isModuleExpanded && visibleChildren.map(
          (child) => this.renderVariable(child, true),
        )}
      </div>
    );
  };

  render() {
    const {
      t, projectPages, elements, idPrefix,
    } = this.props;
    const { expandedPages, filterSelected } = this.state;

    return (
      <div className="tree-view-selector">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <Checkbox
            id={`${idPrefix}-filter-selected`}
            checked={filterSelected}
            onChange={(e) => this.setState({ filterSelected: e.target.checked })}
            className="mt-0"
          >
            {t('project:duplication.filter-selected')}
          </Checkbox>
          <button
            type="button"
            className="btn btn-link btn-sm"
            onClick={this.toggleAllExpanded}
          >
            {t('project:duplication.expand-collapse')}
          </button>
        </div>
        <hr className="mt-0 mb-2" />

        <div className="duplication-tree-view">
          {projectPages.map((page) => {
            const variables = this.getPageVariables(page.key);
            if (variables.length === 0) return null;

            const pageState = this.getPageCheckState(page.key);
            const isExpanded = expandedPages.has(page.key);
            const hasSelected = variables.some(
              (v) => this.getVariableState(v) !== 'none',
            );

            if (filterSelected && !hasSelected) return null;

            return (
              <div key={page.key} className="mb-4">
                <div className="d-flex align-items-center">
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 mr-2"
                    style={{ lineHeight: 1, marginTop: '-4px' }}
                    onClick={() => this.toggleExpanded(page.key)}
                  >
                    <FontAwesomeIcon
                      icon={['far', isExpanded ? 'chevron-down' : 'chevron-right']}
                      transform="shrink-3"
                    />
                  </button>
                  <Checkbox
                    id={`${idPrefix}-page-${page.key}`}
                    checked={pageState.checked}
                    indeterminate={pageState.indeterminate}
                    onChange={
                      (e) => this.togglePage(page.key, e.target.checked)
                    }
                    className="mt-0 mb-2"
                  >
                    <span
                      className="cursor-pointer duplication-tree-label font-weight-bold"
                      role="button"
                      tabIndex={0}
                    >
                      {page.value}
                    </span>
                  </Checkbox>
                </div>
                {isExpanded && (
                  <div style={{ marginLeft: '2.5rem' }}>
                    {variables.map((pe) => {
                      const element = elements[pe.element];
                      if (!element) return null;
                      if (filterSelected && this.getVariableState(pe) === 'none') {
                        return null;
                      }
                      if (element.type === ELEMENT_TYPE_MODULE) {
                        return this.renderModuleWithChildren(pe);
                      }
                      return this.renderVariable(pe);
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }
}

TreeViewSelector.propTypes = {
  t: PropTypes.func.isRequired,
  projectPages: PropTypes.arrayOf(PropTypes.object).isRequired,
  projectElements: PropTypes.arrayOf(PropTypes.object).isRequired,
  elements: PropTypes.objectOf(PropTypes.object).isRequired,
  initialSelectedIds: PropTypes.arrayOf(PropTypes.number),
  idPrefix: PropTypes.string,
};

TreeViewSelector.defaultProps = {
  initialSelectedIds: [],
  idPrefix: 'tv',
};

export default TreeViewSelector;
